import { memo, useMemo, useState } from "react";
import { Comment_Base } from "~/common/typescript/models/Comment";
import FormItemWrapper, { FormItem_Styles } from "~/components/common/FormItemWrapper/FormItemWrapper";
import { Button, Input } from "antd";
import { CommentAPIs } from "~/common/typescript/apis/CommentAPIs";
import { useAtom, useAtomValue } from 'jotai';
import { commentsAtom } from "~/components/Map/Comment/CommentStore";
import RichTextEditor, {
  extractMentionedUserIdsFromHtml,
  MentionUserItem,
} from "~/components/common/RichText";
import { projectMembersWithRolesAtom } from "~/components/Map/Member/MemberStore";
import { currentProjectAtom } from "~/stores/ProjectStore";
import { AC_Project } from "~/common/typescript/services/access_control/AC_Project";

interface CommentItemUpdateFormProps {
  comment: Comment_Base;
  callbackSaved: () => void;
  callbackCanceled?: () => void;
}

const CommentItemUpdateForm = (props: CommentItemUpdateFormProps) => {
  const [comments, setComments] = useAtom(commentsAtom);
  const membersWithRoles = useAtomValue(projectMembersWithRolesAtom);
  const currentProject = useAtomValue(currentProjectAtom);

  const [editingTitle, setEditingTitle] = useState(props.comment.title || '');
  const [editingDescription, setEditingDescription] = useState(props.comment.description || '');
  const [saving, setSaving] = useState(false);

  const mentionUsers = useMemo<MentionUserItem[]>(() => {
    const map = new Map<number, MentionUserItem>();

    if (currentProject?.owner?.id) {
      map.set(currentProject.owner.id, {
        id: currentProject.owner.id,
        fullname: currentProject.owner.fullname || currentProject.owner.username || `User #${currentProject.owner.id}`,
        username: currentProject.owner.username,
        email: currentProject.owner.email,
        roleLabel: 'Chủ dự án',
      });
    }

    membersWithRoles.forEach((item) => {
      if (item?.user?.id) {
        const firstRole = item.roles?.[0];
        const roleLabel = firstRole ? AC_Project.getRoleName(firstRole).vi : 'Thành viên';
        map.set(item.user.id, {
          id: item.user.id,
          fullname: item.user.fullname || item.user.username || `User #${item.user.id}`,
          username: item.user.username,
          email: item.user.email,
          roleLabel,
        });
      }
    });

    comments.forEach((c) => {
      if (c?.user?.id && !map.has(c.user.id)) {
        map.set(c.user.id, {
          id: c.user.id,
          fullname: c.user.fullname || c.user.username || `User #${c.user.id}`,
          username: c.user.username,
          email: c.user.email,
          roleLabel: 'Thành viên',
        });
      }
    });

    return Array.from(map.values());
  }, [currentProject, membersWithRoles, comments]);

  const onClick_Save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const mentionedUserIds = extractMentionedUserIdsFromHtml(editingDescription);
      const res = await CommentAPIs.remote_updateComment(props.comment.id!, {
        title: editingTitle,
        description: editingDescription,
        mentionedUserIds,
      });

      const updatedData = res?.data;
      setComments(
        comments.map((comment: Comment_Base) => {
          if (comment.id === props.comment.id) {
            return {
              ...comment,
              ...(updatedData || {}),
              title: editingTitle,
              description: editingDescription,
              mentionedUserIds,
            };
          }
          return comment;
        }),
      );

      if (mentionedUserIds.length > 0 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('geocloud:refresh-notifications'));
      }

      props.callbackSaved();
    } finally {
      setSaving(false);
    }
  };

  const onClick_Cancel = () => {
    if (props.callbackCanceled) {
      props.callbackCanceled();
    } else {
      props.callbackSaved();
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} data-testid="comment-update-form">
      <FormItemWrapper label="Tiêu đề">
        <Input
          data-testid="comment-title-input"
          value={editingTitle}
          placeholder="Nhập tiêu đề ghi chú..."
          onChange={(e) => setEditingTitle(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey || e.altKey) && e.key === 'Enter') {
              e.preventDefault();
              onClick_Save();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onClick_Cancel();
            }
          }}
        />
      </FormItemWrapper>

      <FormItemWrapper label="Nội dung">
        <RichTextEditor
          content={editingDescription}
          handleChangeContent={(html) => setEditingDescription(html)}
          mentionUsers={mentionUsers}
          compact
          minHeight={72}
          placeholder="Nhập nội dung... (gõ @ để nhắc tên, hỗ trợ **in đậm**)"
          onSubmitShortcut={onClick_Save}
          onCancelShortcut={onClick_Cancel}
        />
      </FormItemWrapper>

      <FormItem_Styles.UI_ButtonGroup style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
        <Button size="small" data-testid="comment-cancel-btn" onClick={onClick_Cancel}>
          Huỷ
        </Button>
        <Button size="small" type="primary" loading={saving} data-testid="comment-save-btn" onClick={onClick_Save}>
          Lưu
        </Button>
      </FormItem_Styles.UI_ButtonGroup>
    </div>
  );
};

export default memo(CommentItemUpdateForm);
